import { apiRequest, apiUpload } from './api';

export interface MaintenanceInfaq {
  id: string;
  assetId: string;
  assetKind?: string;
  title: string;
  reason: string;
  description: string;
  damagePhotoUrls?: string[] | null;
  targetAmount: number | string;
  raisedAmount: number | string;
  requiresInvoice: boolean;
  tireChange: boolean;
  submissionInvoiceUrls?: string[] | null;
  slug?: string | null;
  status:
    | 'PENDING_REVIEW'
    | 'OPEN'
    | 'CLOSED'
    | 'COMPLETED'
    | 'REJECTED'
    | 'WITHDRAWN';
  approvedAt?: string | null;
  maxEndDate?: string | null;
  closedAt?: string | null;
  completedAt?: string | null;
  rejectionReason?: string | null;
  closingReport?: any;
  closingStatus?: 'SUBMITTED' | 'APPROVED' | 'REJECTED' | null;
  closingRejectionReason?: string | null;
  disbursedAmount?: number | string;
  disbursedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  provider?: { companyName: string };
  donations?: any[];
}

export interface CreateMaintenanceInfaqInput {
  assetId: string;
  title: string;
  reason: string;
  description: string;
  targetAmount: number;
  tireChange?: boolean;
  damagePhotoUrls?: string[];
  submissionInvoiceUrls?: string[];
}

export interface ClosingReportInput {
  actualCost: number;
  workDescription: string;
  receiptUrls: string[];
  notes?: string;
}

export interface PublicCampaign {
  id: string;
  slug: string;
  title: string;
  reason: string;
  providerName: string;
  targetAmount: number;
  raisedAmount: number;
  donorCount: number;
  daysLeft: number | null;
}

export interface PublicCampaignDetail extends PublicCampaign {
  description: string;
  damagePhotoUrls?: string[] | null;
  requiresInvoice: boolean;
  tireChange: boolean;
  donations: Array<{
    donorName: string;
    amount: number;
    isAnonymous: boolean;
    createdAt: string;
  }>;
}

class MaintenanceInfaqService {
  // ─── Uploads ─────────────────────────────────────────────────────────
  async uploadFile(file: File): Promise<string> {
    const fd = new FormData();
    fd.append('file', file);
    const r = await apiUpload<{ url: string }>('/maintenance-infaq/upload', fd);
    return r.data.url;
  }

  // ─── Provider ─────────────────────────────────────────────────────────
  async create(payload: CreateMaintenanceInfaqInput): Promise<MaintenanceInfaq> {
    const r = await apiRequest.post<MaintenanceInfaq>('/maintenance-infaq', payload);
    return r.data;
  }

  async listMine(status?: string): Promise<MaintenanceInfaq[]> {
    const r = await apiRequest.get<MaintenanceInfaq[]>('/maintenance-infaq', status ? { status } : undefined);
    return r.data;
  }

  async getMine(id: string): Promise<MaintenanceInfaq> {
    const r = await apiRequest.get<MaintenanceInfaq>(`/maintenance-infaq/${id}`);
    return r.data;
  }

  async withdraw(id: string): Promise<MaintenanceInfaq> {
    const r = await apiRequest.post<MaintenanceInfaq>(`/maintenance-infaq/${id}/withdraw`);
    return r.data;
  }

  async submitClosingReport(id: string, payload: ClosingReportInput): Promise<MaintenanceInfaq> {
    const r = await apiRequest.post<MaintenanceInfaq>(
      `/maintenance-infaq/${id}/closing-report`,
      payload,
    );
    return r.data;
  }

  // ─── Admin ────────────────────────────────────────────────────────────
  async adminListAll(status?: string): Promise<MaintenanceInfaq[]> {
    const r = await apiRequest.get<MaintenanceInfaq[]>(
      '/maintenance-infaq/admin/all',
      status ? { status } : undefined,
    );
    return r.data;
  }

  async adminApprove(id: string): Promise<MaintenanceInfaq> {
    const r = await apiRequest.post<MaintenanceInfaq>(`/maintenance-infaq/admin/${id}/approve`);
    return r.data;
  }

  async adminReject(id: string, reason: string): Promise<MaintenanceInfaq> {
    const r = await apiRequest.post<MaintenanceInfaq>(`/maintenance-infaq/admin/${id}/reject`, { reason });
    return r.data;
  }

  async adminApproveClosing(id: string): Promise<MaintenanceInfaq> {
    const r = await apiRequest.post<MaintenanceInfaq>(`/maintenance-infaq/admin/${id}/approve-closing`);
    return r.data;
  }

  async adminRejectClosing(id: string, reason: string): Promise<MaintenanceInfaq> {
    const r = await apiRequest.post<MaintenanceInfaq>(`/maintenance-infaq/admin/${id}/reject-closing`, { reason });
    return r.data;
  }

  // ─── Public ───────────────────────────────────────────────────────────
  async listOpenCampaigns(): Promise<PublicCampaign[]> {
    const r = await apiRequest.get<PublicCampaign[]>('/maintenance-infaq/public/campaigns');
    return r.data;
  }

  async getCampaignBySlug(slug: string): Promise<PublicCampaignDetail> {
    const r = await apiRequest.get<PublicCampaignDetail>(`/maintenance-infaq/public/campaigns/${slug}`);
    return r.data;
  }

  async donate(payload: {
    maintenanceInfaqId: string;
    donorName: string;
    donorEmail: string;
    donorPhone: string;
    amount: number;
    isAnonymous?: boolean;
    prayerRequest?: string;
  }): Promise<{ donation: any; redipayDetails: any }> {
    const r = await apiRequest.post<{ donation: any; redipayDetails: any }>(
      '/maintenance-infaq/donate',
      payload,
    );
    return r.data;
  }
}

export const maintenanceInfaqService = new MaintenanceInfaqService();
